import { useEffect, useRef, useState } from "react";
import { Plus, Search } from "lucide-react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";

/**
 * Single-select picker used inside TimerBar for Activities (legacy MetaPicker).
 * Items: `[{ id, name, color }]`. Pass `onCreate` to allow inline creation.
 */
export default function MetaPicker({
  icon: Icon, items, value, onChange, onCreate, placeholder, defaultColor, testId,
}) {
  const selected = items.find((i) => i.id === value);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSearch("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const query = search.trim();
  const filtered = items.filter((i) => i.name.toLowerCase().includes(query.toLowerCase()));
  const exact = items.find((i) => i.name.toLowerCase() === query.toLowerCase());

  const handleCreate = async () => {
    if (!query || exact || !onCreate) return;
    try {
      const created = await onCreate({ name: query, color: defaultColor });
      if (created?.id) onChange(created.id);
      setOpen(false);
    } catch { /* surfaced upstream */ }
  };

  const handleSelect = (id) => { onChange(id); setOpen(false); };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filtered.length === 1) handleSelect(filtered[0].id);
      else if (query && !exact) handleCreate();
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-8 px-2 rounded-md hover:bg-slate-100 text-slate-600 flex items-center gap-1.5 text-xs"
          data-testid={testId}
        >
          {selected ? (
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: selected.color }} />
          ) : (
            <Icon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          )}
          <span className={`max-w-[140px] truncate ${selected ? "" : "text-slate-400"}`}>
            {selected ? selected.name : placeholder}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="end">
        <div className="text-[10px] uppercase tracking-widest text-slate-400 px-2 pt-1 pb-2 flex items-center justify-between">
          <span>{placeholder}</span>
          {selected && (
            <button
              onClick={() => handleSelect(null)}
              className="text-[10px] text-slate-400 hover:text-slate-700 normal-case tracking-normal"
              data-testid={`${testId}-clear`}
            >
              Clear
            </button>
          )}
        </div>
        <div className="relative mb-2">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={`Search or create ${placeholder.toLowerCase()}…`}
            className="w-full text-sm pl-8 pr-2 py-1.5 rounded-md border border-slate-200 focus:border-[#145c8c] focus:outline-none"
            data-testid={`${testId}-search`}
          />
        </div>
        <div className="max-h-60 overflow-y-auto">
          {filtered.length === 0 && !query && (
            <div className="px-2 py-3 text-xs text-slate-400">No {placeholder.toLowerCase()}s yet.</div>
          )}
          {filtered.map((it) => (
            <button
              key={it.id}
              onClick={() => handleSelect(it.id)}
              className={`w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 text-sm ${
                value === it.id ? "bg-slate-100" : ""
              }`}
              data-testid={`${testId}-item-${it.id}`}
            >
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: it.color }} />
              <span className="truncate">{it.name}</span>
            </button>
          ))}
          {query && !exact && onCreate && (
            <button
              onClick={handleCreate}
              className="w-full text-left mt-1 px-2 py-1.5 rounded hover:bg-[#e8f1f8] text-[#0b2c47] flex items-center gap-2 text-sm font-medium border-t border-slate-100 pt-2"
              data-testid={`${testId}-create`}
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Create &quot;{query}&quot;</span>
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
